import React from 'react'

import { DeviceType, useDeviceType } from '@library/dom'

import { CUSTOMER_REVIEWS } from '../../../config'
import { Carousel, type CarouselProps } from '../../EndlessVarietySection/Carousel'
import { CWCReviewsCard } from './CWCReviewsCard'
import { Box, Display } from '@gousto-internal/citrus-react'
import { useIsDesktopBreakpoint } from '../../../hooks/useIsDesktopBreakpoint'
import { LandingPageContentSections } from '../../../tracking'

// Forked from CustomerReviewsSection/components/ReviewsCarousel for this section only —
// renders the local ReviewsCard (corrected box-shadow) instead of the shared one.
const propertiesForTracking = {
  carouselSection: LandingPageContentSections.HOMEPAGE_REVIEWS,
}

// CSS-driven, matching the Large breakpoint (1024px) the rest of this section treats as desktop —
// not the JS-computed useDeviceType() DESKTOP bucket, which starts at 768px (standard iPad width).
const NAV_BUTTONS_DISPLAY: CarouselProps['navButtonsDisplay'] = [
  Display.None,
  null,
  null,
  Display.Block,
]

// Center of the 144px card itself, not the taller slide (which reserves extra space below for
// the card's drop shadow) — see Carousel's navButtonsCenterY.
const NAV_BUTTONS_CENTER_Y: CarouselProps['navButtonsCenterY'] = '76px'

export const CWCReviewsCarousel = () => {
  const deviceType = useDeviceType()
  const isMobileDevice = deviceType === DeviceType.MOBILE
  const isDesktopDevice = useIsDesktopBreakpoint()
  const carouselWrapperStyle = deviceType === DeviceType.TABLET ? { paddingLeft: '8px' } : undefined
  let carouselOptions: CarouselProps['options']

  if (isDesktopDevice) {
    carouselOptions = {
      align: 'center' as const,
      containScroll: 'trimSnaps',
      loop: false,
    }
  } else if (isMobileDevice) {
    carouselOptions = { align: 'center' as const, loop: false }
  } else {
    carouselOptions = { align: 'center' as const, loop: true }
  }

  return (
    <Box data-testid="reviews-carousel" style={carouselWrapperStyle}>
      <Carousel
        carouselAreaLabel="reviewsCarousel"
        options={carouselOptions}
        slidePaddingH={2}
        slidePaddingV={1}
        showNavButtons
        navButtonsDisplay={NAV_BUTTONS_DISPLAY}
        navButtonsCenterY={NAV_BUTTONS_CENTER_Y}
        autoHideNavButtons
        startAtMiddleAvailableSnap={isDesktopDevice || isMobileDevice}
        centerSlidesWhenNotScrollable={isDesktopDevice}
        snowplowTrackingProperties={propertiesForTracking}
      >
        {CUSTOMER_REVIEWS.review.map(({ quote, reviewer }, index) => (
          // The card's drop shadow extends ~24px below it; the shared Carousel clips overflow on
          // both axes, so pad the slide's bottom to keep the shadow from being cut.
          <Box key={`${reviewer}-${index}`} paddingBottom={8}>
            <CWCReviewsCard quote={quote} reviewer={reviewer} />
          </Box>
        ))}
      </Carousel>
    </Box>
  )
}
